import { CheckCircle, PlayCircle } from 'lucide-react';

interface MasterclassProgressBadgeProps {
  completedCases: number;
  totalCases: number;
  /** Whether the user has started this masterclass (visited any slides) */
  hasStarted?: boolean;
}

/**
 * Progress indicator for masterclass cards.
 *
 * Shows different states based on progress:
 * - totalCases=0: "No cases yet"
 * - Not started (hasStarted=false): "Not started"
 * - In progress (hasStarted=true, not completed): "In progress"
 * - Completed: "Completed" with checkmark
 */
export const MasterclassProgressBadge = ({
  completedCases,
  totalCases,
  hasStarted = false,
}: MasterclassProgressBadgeProps) => {
  // Don't show progress if no cases
  if (totalCases === 0) {
    return <p className="text-sm text-muted-foreground">No cases yet</p>;
  }

  const isCompleted = completedCases === totalCases && totalCases > 0;

  // Completed state
  if (isCompleted) {
    return (
      <div className="flex items-center gap-2 text-green-600">
        <CheckCircle className="h-4 w-4" />
        <span className="text-sm font-medium">Completed</span>
      </div>
    );
  }

  // In progress state
  if (hasStarted) {
    return (
      <div className="flex items-center gap-2 text-livelinx-blue300">
        <PlayCircle className="h-4 w-4" />
        <span className="text-sm font-medium">In progress</span>
      </div>
    );
  }

  // Not started state
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground">Not started</span>
    </div>
  );
};
